import { useMemo } from 'react'
import { useAccount } from 'wagmi'

export enum WalletRole {
  ADMIN = 'admin',
  PROVIDER = 'provider',
}

const DEVS_ADMIN_ADDRESSES =
  process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENV === 'staging'
    ? [
        '0xf4f28512D765dDd7f222584B78B1b451d6768c1C',
        '0x8be1038D11c19F86071363E818A890014cBf3433',
        '0x26D240ab93e80C1846B9969297aD345bF6360BbB',
      ]
    : []

export const WALLET_ROLES: Record<WalletRole, readonly string[]> = {
  [WalletRole.ADMIN]: [
    ...DEVS_ADMIN_ADDRESSES,
    '0x818664bBe2913b7cCED71352F6912CcC0e376880',
    '0x84f070c79290435C8C3b6074Da279C7d8ab9b58f', // allowed for liquidity
    '0x36400517548EDc3D1c379057A8edaD9Ed9Bda5A1', // master admin
  ] as const,
  [WalletRole.PROVIDER]: [
    '0x8dE1C528B449981C475A8C63D84aa4410224a64B',
    '0x0aC80D967AD8aEd21a078bD4314aa78840aEC7E2', // rebalancer testnet
    '0xAe5ddaF3b77d2e26725D0Eda7572782E36a61AD5', // rebalancer mainnet
    '0xdFfceF6E03873ea091ac10bE4381Cdf65305a1DE',
  ] as const,
} as const

export const getWalletsByRole = (role: WalletRole): readonly string[] => {
  return WALLET_ROLES[role] || []
}

export const getAllWalletsWithRoles = (): readonly string[] => {
  const wallets = Object.values(WALLET_ROLES).flat()
  return [...new Set(wallets)]
}

export const getRolesForWallet = (address: string | undefined | null): WalletRole[] => {
  if (!address) return []

  const normalizedAddress = address.toLowerCase()
  const roles: WalletRole[] = []

  for (const [role, addresses] of Object.entries(WALLET_ROLES)) {
    if (addresses.some((addr) => addr.toLowerCase() === normalizedAddress)) {
      roles.push(role as WalletRole)
    }
  }

  return roles
}

export const hasRole = (address: string | undefined | null, role: WalletRole) => {
  if (!address) return false

  const wallets = getWalletsByRole(role)
  return wallets.some((allowedAddress) => allowedAddress.toLowerCase() === address.toLowerCase())
}

export const hasAnyRole = (address: string | undefined | null, roles: WalletRole[]) => {
  if (!address || roles.length === 0) return false

  return roles.some((role) => hasRole(address, role))
}

export const hasAllRoles = (address: string | undefined | null, roles: WalletRole[]) => {
  if (!address || roles.length === 0) return false

  return roles.every((role) => hasRole(address, role))
}

const useWalletAddress = () => {
  const { address, isConnected } = useAccount()
  return useMemo(() => (isConnected && address ? address : null), [address, isConnected])
}

export const useHasRole = (role: WalletRole) => {
  const address = useWalletAddress()
  return useMemo(() => hasRole(address, role), [address, role])
}

export const useHasAnyRole = (roles: WalletRole[]) => {
  const address = useWalletAddress()
  return useMemo(() => hasAnyRole(address, roles), [address, roles])
}

export const useHasAllRoles = (roles: WalletRole[]) => {
  const address = useWalletAddress()
  return useMemo(() => hasAllRoles(address, roles), [address, roles])
}

export const useWalletRoles = (): WalletRole[] => {
  const address = useWalletAddress()
  return useMemo(() => getRolesForWallet(address), [address])
}

export const useIsAdmin = () => {
  return useHasRole(WalletRole.ADMIN)
}

export const useIsLiquidityProvider = () => {
  return useHasAnyRole([WalletRole.ADMIN, WalletRole.PROVIDER])
}

export const useIsAllowedWallet = () => {
  return useIsAdmin()
}

export const getAllowedWallets = (): readonly string[] => {
  return getAllWalletsWithRoles()
}
export const isWalletAllowed = (address: string | undefined | null) => {
  return hasRole(address, WalletRole.ADMIN)
}
